import { capitalize } from 'lodash';

import { ASSET_TYPE, type AssetType } from 'src/data/asset';
import { CHANNEL, EMAILS_CHANNELS } from 'src/data/channel';
import { RESOURCE_DOCUMENT } from 'src/data/performance';
import type { ID } from 'src/lib/collection';
import { formatPercentageWithOneDecimal } from 'src/lib/format';

import { USER_GOAL_PROPS, type UserGoal } from './const';
import type {
  BenchmarkConfig,
  BenchmarkConfigSingle,
  ComparisonSet,
  ExampleWithMetric,
} from './types';

/**
 * Retrieves the channel associated with a specific asset type.
 *
 * @param assetType - The asset type identifier.
 * @returns The corresponding channel or null if not found.
 */
export const getChannelFromAssetType = (assetType: AssetType) => {
  switch (assetType) {
    case ASSET_TYPE.facebookPrimaryText:
    case ASSET_TYPE.facebookHeadline:
    case ASSET_TYPE.facebookMixtureAd:
      return CHANNEL.facebook;
    case ASSET_TYPE.facebookPost:
      return CHANNEL.facebookPages;
    case ASSET_TYPE.googleDescription:
    case ASSET_TYPE.googleHeadline:
    case ASSET_TYPE.googleMediumHeadline:
    case ASSET_TYPE.googleLongHeadline:
    case ASSET_TYPE.googleShortDescription:
      return CHANNEL.google;
    case ASSET_TYPE.linkedinSingleImageHeadline:
    case ASSET_TYPE.linkedinSingleImageIntro:
    case ASSET_TYPE.linkedinMixtureAd:
      return CHANNEL.linkedin;
    case ASSET_TYPE.emailSubjectLine:
    case ASSET_TYPE.emailPromotionalBody:
    case ASSET_TYPE.emailPromotionalSubjectLine:
    case ASSET_TYPE.emailColdOutreachBody:
    case ASSET_TYPE.emailColdOutreachSubjectLine:
    case ASSET_TYPE.emailSubject:
    case ASSET_TYPE.emailBody:
      return CHANNEL.emailChannel;
    case ASSET_TYPE.instagramCaption:
      return CHANNEL.instagram;
    case ASSET_TYPE.linkedinPost:
      return CHANNEL.linkedinSocial;
    case ASSET_TYPE.tweet:
      return CHANNEL.twitterSocial;
    default:
      return null;
  }
};

/**
 * Retrieves email channels associated with a specific asset type.
 *
 * @param assetType - The asset type identifier.
 * @returns The array of email channels associated with the asset type.
 */
export const getEmailChannelsFromAssetType = (assetType: AssetType) => {
  switch (assetType) {
    case ASSET_TYPE.emailPromotionalBody:
      return [RESOURCE_DOCUMENT.emailPromotionCsvResource, ...EMAILS_CHANNELS];
    case ASSET_TYPE.emailColdOutreachBody:
      return [RESOURCE_DOCUMENT.emailColdCsvResource, ...EMAILS_CHANNELS];
    case ASSET_TYPE.emailSubject:
      return [RESOURCE_DOCUMENT.emailSubjectLineCsvResource, ...EMAILS_CHANNELS];
    case ASSET_TYPE.emailBody:
      return [
        RESOURCE_DOCUMENT.emailPromotionCsvResource,
        RESOURCE_DOCUMENT.emailColdCsvResource,
        ...EMAILS_CHANNELS,
      ];
    case ASSET_TYPE.emailMixture:
      return [
        RESOURCE_DOCUMENT.emailSubjectLineCsvResource,
        RESOURCE_DOCUMENT.emailPromotionCsvResource,
        RESOURCE_DOCUMENT.emailColdCsvResource,
        ...EMAILS_CHANNELS,
      ];
    default:
      return EMAILS_CHANNELS;
  }
};

/**
 * Merges two benchmark configurations.
 *
 * @param benchmarkConfig - The original benchmark configuration.
 * @param newBenchmarkConfig - The new benchmark configuration to merge.
 * @returns The merged benchmark configuration.
 */
export const mergeBenchmarkConfigs = (
  benchmarkConfig: BenchmarkConfigSingle,
  newBenchmarkConfig: BenchmarkConfigSingle
): BenchmarkConfigSingle => {
  const mergedComparisonSet =
    benchmarkConfig?.comparisonSet && newBenchmarkConfig?.comparisonSet
      ? {
          ...benchmarkConfig.comparisonSet,
          ...newBenchmarkConfig.comparisonSet,
        }
      : newBenchmarkConfig?.comparisonSet || benchmarkConfig?.comparisonSet;

  return {
    ...benchmarkConfig,
    ...newBenchmarkConfig,
    comparisonSet: mergedComparisonSet,
  };
};

/**
 * Retrieves the label associated with a user goal.
 *
 * @param userGoal - The user goal identifier.
 * @returns The label associated with the user goal.
 */
export const getUserGoalLabel = (userGoal: UserGoal): string =>
  USER_GOAL_PROPS[userGoal]?.label || capitalize(userGoal as string).replace(/_/g, ' ');

/**
 * Checks if a user goal is expressed as a percentage.
 *
 * @param userGoal - The user goal identifier.
 * @returns True if the user goal is expressed as a percentage, false otherwise.
 */
export const getIsPercentageUserGoal = (userGoal: UserGoal): boolean =>
  USER_GOAL_PROPS[userGoal]?.isPercentage || false;

/**
 * Checks if a user goal's metric value should be multiplied.
 *
 * @param {string} userGoal - The user goal identifier.
 * @returns {boolean} - True if the metric value should be multiplied, false otherwise.
 */
export const getShouldMultipleMetricValue = (userGoal: UserGoal): boolean =>
  USER_GOAL_PROPS[userGoal]?.shouldMultipleMetricValue || false;

/**
 * Retrieves the percentage representation of a user goal metric.
 *
 * @param userGoal - The user goal identifier.
 * @param metric - The metric value to format.
 * @returns The formatted percentage representation of the metric.
 */
export const getPercentageUserGoalMetric = (userGoal: UserGoal, metric: number): string => {
  if (getShouldMultipleMetricValue(userGoal)) {
    return formatPercentageWithOneDecimal(metric * 100);
  } else {
    return formatPercentageWithOneDecimal(metric);
  }
};

/**
 * Aligns examples by filtering out deleted examples from custom comparison sets.
 *
 * @param customComparisonSets - The array of custom comparison sets.
 * @param deletedExamples - The array of deleted examples.
 * @returns The aligned examples.
 */
export const alignExamples = (
  customComparisonSets: ComparisonSet[],
  deletedExamples: string[]
): string[][] => {
  return customComparisonSets.map((customComparisonSet) =>
    customComparisonSet.examples.reduce((result: string[], example: string) => {
      if (!deletedExamples.includes(example)) {
        result.push(example);
      }
      return result;
    }, [])
  );
};

/**
 * Aligns examples with metrics by filtering out deleted examples from custom comparison sets.
 *
 * @param customComparisonSets - The array of custom comparison sets.
 * @param deletedExamples - The array of deleted examples.
 * @returns The aligned examples with metrics.
 */
export const alignExamplesWithMetrics = (
  customComparisonSets: ComparisonSet[],
  deletedExamples: string[]
): ExampleWithMetric[][] => {
  return customComparisonSets.map((customComparisonSet) => {
    if (!customComparisonSet.examplesWithMetrics) {
      return [];
    }

    return customComparisonSet.examplesWithMetrics.reduce(
      (result: ExampleWithMetric[], exampleWithMetrics) => {
        if (!deletedExamples.includes(exampleWithMetrics.text)) {
          result.push(exampleWithMetrics);
        }
        return result;
      },
      []
    );
  });
};

/**
 * Retrieves a comparison set from the list based on criteria in the benchmark configuration.
 *
 * @param benchmarkConfig - The benchmark configuration object.
 * @param comparisonSets - The array of comparison sets to search from.
 * @returns The matching comparison set, or null if not found.
 */
export const getComparisonSetFromBenchmarkConfigSingle = (
  benchmarkConfig: BenchmarkConfigSingle,
  comparisonSets: ComparisonSet[]
) => {
  if (!benchmarkConfig || !comparisonSets?.length) {
    return undefined;
  }

  // Find the comparison set that matches userGoal and name in benchmarkConfig
  return comparisonSets.find(
    (comparisonSet) =>
      comparisonSet?.userGoal === benchmarkConfig.comparisonSet?.userGoal &&
      comparisonSet?.name === benchmarkConfig.comparisonSet?.name
  );
};

/**
 * Get a list of comparison sets from benchmark configurations based on account ID and asset type.
 *
 * @param configurationList - The array of benchmark configurations.
 * @param accountId - The ID of the account to filter by.
 * @param assetType - The asset type to filter by.
 * @returns The comparison sets associated with the matching benchmark configuration, or an empty array if none found.
 */
export const getComparisonSetsFromConfigList = (
  configurationList: BenchmarkConfig[],
  accountId: ID,
  assetType: AssetType
): ComparisonSet[] => {
  if (!Array.isArray(configurationList) || configurationList.length === 0) {
    return [];
  }

  const config = configurationList.find(
    (item) => item.account.id === accountId && item.assetType === assetType
  );
  return config?.comparisonSets || [];
};

/**
 * Retrieves the selected comparison set based on the benchmark configuration.
 *
 * @param benchmarkConfig - The benchmark configuration object.
 * @param comparisonSets - The optional array of comparison sets.
 * @returns The selected comparison set.
 */
export const getSelectedComparisonSet = (
  benchmarkConfig: BenchmarkConfigSingle,
  comparisonSets: ComparisonSet[]
) => {
  return (
    benchmarkConfig.comparisonSet ||
    getComparisonSetFromBenchmarkConfigSingle(benchmarkConfig, comparisonSets) ||
    comparisonSets?.[0]
  );
};
